import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  EmptyState,
  SearchableSelect,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@jem-open/jem-ui';
import { PiggyBank, RefreshCw } from 'lucide-react';
import SavingsBatchTable from './SavingsBatchTable';
import PaymentsTable from './PaymentsTable';
import TablePagination from './TablePagination';
import SubmissionBreakdownModal from './SubmissionBreakdownModal';
import type { BreakdownTarget } from './SubmissionBreakdownModal';
import CreatePaymentDialog from './CreatePaymentDialog';
import SavingsBatchItemsModal from './SavingsBatchItemsModal';
import {
  downloadBatch,
  downloadOutstandingCsv,
  downloadSubmissionCsv,
  getPendingSummary,
  listBatches,
  listSubmissions,
  submitPending,
} from './services/savingsBatchService';
import type {
  PayCycleFrequency,
  PendingSummary,
  ProviderPaymentSubmission,
  SavingPaymentBatch,
} from './types';

const PAGE_TABS = {
  PAYMENTS: 'payments',
  BATCHES: 'batches',
};

const PAGE_SIZE = 10;

const SavingsBatches = () => {
  const [activeTab, setActiveTab] = useState(PAGE_TABS.PAYMENTS);

  const [submissions, setSubmissions] = useState<ProviderPaymentSubmission[]>(
    []
  );
  const [pendingSummary, setPendingSummary] = useState<PendingSummary | null>(
    null
  );
  const [paymentsLoading, setPaymentsLoading] = useState(true);

  const [batches, setBatches] = useState<SavingPaymentBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [employerFilter, setEmployerFilter] = useState<string>('');
  const [periodFilter, setPeriodFilter] = useState<string>('');
  const [payCycleFilter, setPayCycleFilter] = useState<string>('');

  const [paymentPage, setPaymentPage] = useState(1);
  const [batchPage, setBatchPage] = useState(1);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [breakdownTarget, setBreakdownTarget] =
    useState<BreakdownTarget | null>(null);

  const [itemsTarget, setItemsTarget] = useState<SavingPaymentBatch | null>(
    null
  );

  // Monotonic request id so out-of-order responses (a slow earlier call
  // resolving after a newer one) can't backfill stale payment data.
  const paymentsRequestId = useRef(0);

  const loadPayments = () => {
    paymentsRequestId.current += 1;
    const requestId = paymentsRequestId.current;
    setPaymentsLoading(true);
    Promise.all([listSubmissions(), getPendingSummary()])
      .then(([subs, summary]) => {
        if (requestId !== paymentsRequestId.current) return;
        setSubmissions(subs);
        setPendingSummary(summary);
      })
      .catch(() => {
        if (requestId !== paymentsRequestId.current) return;
        // Clear stale data so a failed reload can't leave outdated payments on
        // screen behind the error.
        setSubmissions([]);
        setPendingSummary(null);
        setError('Failed to load payments. Please retry.');
      })
      .finally(() => {
        if (requestId === paymentsRequestId.current) setPaymentsLoading(false);
      });
  };

  const loadBatches = () => {
    setLoading(true);
    listBatches({
      employerId: employerFilter ? Number(employerFilter) : null,
      period: periodFilter || null,
      payCycleFrequency: payCycleFilter
        ? (payCycleFilter as PayCycleFrequency)
        : null,
    })
      .then((data) => setBatches(data))
      .catch(() => setError('Failed to load savings batches. Please retry.'))
      .finally(() => setLoading(false));
  };

  const load = () => {
    setError(null);
    loadPayments();
    loadBatches();
  };

  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setError(null);
    setBatchPage(1);
    loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employerFilter, periodFilter, payCycleFilter]);

  const handleCreatePaymentConfirm = async () => {
    setSubmitting(true);
    try {
      await submitPending();
      setCreateDialogOpen(false);
      load();
    } catch {
      // A provider failure still persists a FAILED submission row, so reload to
      // surface it in the history below.
      setError('Failed to create payment. Please review the payment history.');
      setCreateDialogOpen(false);
      loadPayments();
    } finally {
      setSubmitting(false);
    }
  };

  const employerOptions = Array.from(
    new Map(
      batches.map((b) => [b.employerId.toString(), b.employerName])
    ).entries()
  ).map(([id, name]) => ({ value: id, label: name }));

  const periodOptions = Array.from(new Set(batches.map((b) => b.period)))
    .sort()
    .reverse()
    .map((p) => ({ value: p, label: p }));

  const payCycleOptions: { value: PayCycleFrequency; label: string }[] = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'fortnightly', label: 'Fortnightly' },
  ];

  const paymentPageCount = Math.max(
    1,
    Math.ceil(submissions.length / PAGE_SIZE)
  );
  const safePaymentPage = Math.min(paymentPage, paymentPageCount);
  const pagedSubmissions = submissions.slice(
    (safePaymentPage - 1) * PAGE_SIZE,
    safePaymentPage * PAGE_SIZE
  );

  const batchPageCount = Math.max(1, Math.ceil(batches.length / PAGE_SIZE));
  const safeBatchPage = Math.min(batchPage, batchPageCount);
  const pagedBatches = batches.slice(
    (safeBatchPage - 1) * PAGE_SIZE,
    safeBatchPage * PAGE_SIZE
  );

  return (
    <div className="min-h-screen bg-white p-md">
      <header
        className="-mx-md -mt-md mb-md flex flex-wrap items-center justify-between gap-sm px-md py-lg"
        style={{ backgroundColor: 'rgba(237,244,251,0.5)' }}>
        <div>
          <h1 className="m-none font-heading text-2xl font-semibold text-greyscale-text-title">
            Savings Batches
          </h1>
          <p className="m-none mt-xxs max-w-2xl text-sm text-greyscale-text-body">
            Review the outstanding savings pool and submit provider payments on
            demand, with a full history of every payment made.
          </p>
        </div>
        <Button
          leftIcon={<RefreshCw className="size-4" />}
          onClick={load}
          loading={loading || paymentsLoading}>
          Refresh
        </Button>
      </header>

      {error && (
        <Alert variant="destructive" className="mb-md">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList variant="line">
          <TabsTrigger variant="line" value={PAGE_TABS.PAYMENTS}>
            Payments
          </TabsTrigger>
          <TabsTrigger variant="line" value={PAGE_TABS.BATCHES}>
            All batches
          </TabsTrigger>
        </TabsList>

        <TabsContent value={PAGE_TABS.PAYMENTS} className="mt-md">
          <PaymentsTable
            submissions={pagedSubmissions}
            pendingSummary={pendingSummary}
            isLoading={paymentsLoading}
            isSubmitting={submitting}
            onCreatePayment={() => setCreateDialogOpen(true)}
            onViewSubmission={(submission) => setBreakdownTarget(submission)}
            onViewOutstanding={() => setBreakdownTarget('pending')}
            onDownloadSubmission={(submission) =>
              downloadSubmissionCsv(submission.id)
            }
            onDownloadOutstanding={() => downloadOutstandingCsv()}
          />
          <TablePagination
            page={safePaymentPage}
            pageCount={paymentPageCount}
            onPageChange={setPaymentPage}
          />
        </TabsContent>

        <TabsContent value={PAGE_TABS.BATCHES} className="mt-md">
          <div className="mb-md mt-md flex flex-wrap items-end gap-3">
            <div className="w-64">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Employer
              </span>
              <SearchableSelect
                options={[
                  { value: '', label: 'All employers' },
                  ...employerOptions,
                ]}
                value={employerFilter}
                onValueChange={(value) => setEmployerFilter(value)}
                placeholder="All employers"
                searchPlaceholder="Search..."
                emptyMessage="No match"
              />
            </div>
            <div className="w-48">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Period
              </span>
              <SearchableSelect
                options={[
                  { value: '', label: 'All periods' },
                  ...periodOptions,
                ]}
                value={periodFilter}
                onValueChange={(value) => setPeriodFilter(value)}
                placeholder="All periods"
                searchPlaceholder="Search..."
                emptyMessage="No match"
              />
            </div>
            <div className="w-48">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Pay Cycle
              </span>
              <SearchableSelect
                options={[
                  { value: '', label: 'All pay cycles' },
                  ...payCycleOptions,
                ]}
                value={payCycleFilter}
                onValueChange={(value) => setPayCycleFilter(value)}
                placeholder="All pay cycles"
                searchPlaceholder="Search..."
                emptyMessage="No match"
              />
            </div>
          </div>

          {!loading && batches.length === 0 && !error ? (
            <EmptyState
              icon={<PiggyBank className="size-10" />}
              title="No savings batches yet"
              description="Batches will appear here once the recon cron generates them."
            />
          ) : (
            <>
              <SavingsBatchTable
                batches={pagedBatches}
                isLoading={loading}
                onDownload={(batch) => downloadBatch(batch.id)}
                onViewItems={(batch) => setItemsTarget(batch)}
              />
              <TablePagination
                page={safeBatchPage}
                pageCount={batchPageCount}
                onPageChange={setBatchPage}
              />
            </>
          )}
        </TabsContent>
      </Tabs>

      <SubmissionBreakdownModal
        target={breakdownTarget}
        open={breakdownTarget !== null}
        onOpenChange={(open) => !open && setBreakdownTarget(null)}
      />

      <CreatePaymentDialog
        pendingSummary={pendingSummary}
        open={createDialogOpen}
        onOpenChange={(open) => !open && setCreateDialogOpen(false)}
        onConfirm={handleCreatePaymentConfirm}
        isSubmitting={submitting}
      />

      <SavingsBatchItemsModal
        batch={itemsTarget}
        open={itemsTarget !== null}
        onOpenChange={(open) => !open && setItemsTarget(null)}
      />
    </div>
  );
};

export default SavingsBatches;
